import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Tab, TabList, Tabs, Typography } from '@mui/joy';

export const DIRECT_MAIL_ROUTES = {
  dashboard: '/marketing/direct-mail',
  map: '/marketing/direct-mail/map',
  queue: '/marketing/direct-mail/queue',
  campaigns: '/marketing/direct-mail/campaigns',
  batches: '/marketing/direct-mail/batches',
  settings: '/marketing/direct-mail/settings',
} as const;

const NAV_ITEMS: { path: string; label: string }[] = [
  { path: DIRECT_MAIL_ROUTES.dashboard, label: 'Overview' },
  { path: DIRECT_MAIL_ROUTES.map, label: 'Map' },
  { path: DIRECT_MAIL_ROUTES.queue, label: 'Queue' },
  { path: DIRECT_MAIL_ROUTES.campaigns, label: 'Campaigns' },
  { path: DIRECT_MAIL_ROUTES.batches, label: 'Batches' },
  { path: DIRECT_MAIL_ROUTES.settings, label: 'Settings' },
];

export const DirectMailNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The overview lives at the section root, so an exact match has to win before
  // the prefix test or every sub-page would also light up "Overview".
  const active =
    NAV_ITEMS.find((item) => item.path === location.pathname)?.path ??
    NAV_ITEMS.slice(1).find((item) => location.pathname.startsWith(item.path))?.path ??
    DIRECT_MAIL_ROUTES.dashboard;

  return (
    <Tabs
      value={active}
      onChange={(_, value) => {
        if (typeof value === 'string' && value !== location.pathname) navigate(value);
      }}
      sx={{ bgcolor: 'transparent', mb: 2 }}
    >
      <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
        {NAV_ITEMS.map((item) => (
          <Tab key={item.path} value={item.path}>
            {item.label}
          </Tab>
        ))}
      </TabList>
    </Tabs>
  );
};

export interface DirectMailPageHeaderProps {
  title: string;
  description: string;
  actions?: React.ReactNode;
}

export const DirectMailPageHeader: React.FC<DirectMailPageHeaderProps> = ({
  title,
  description,
  actions,
}) => (
  <Box sx={{ mb: 2 }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      sx={{ mb: 1 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography level="h3" sx={{ fontSize: { xs: '1.15rem', sm: '1.4rem' } }}>
          {title}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Stack>
    <DirectMailNav />
  </Box>
);
